import React, { useMemo } from "react"
import * as pdfjsLib from "pdfjs-dist"
import Step1MonthSelector from "./components/Step1MonthSelector"
import Step2FileDropzone from "./components/Step2FileDropzone"
import Step3ExtractionTable from "./components/step3/Step3ExtractionTable"
import Step3ExtractionVerificationModal from "./components/step3/Step3ExtractionVerificationModal"
import MassSaisieSubmissionError from "./components/MassSaisieSubmissionError"
import { useMassSaisie } from "./useMassSaisie"

// Initialisation du worker PDF.js
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString()

const SaisieEnMasseDesDeclarationsParRespFournisseur = () => {
     const { state, setters, handlers, fileInputRef } = useMassSaisie()

     /**
      * Liste à plat de tous les fichiers pour la navigation du visualiseur (Modal).
      * Gère la facture unique ET les justificatifs multiples.
      */
     const allPreviewableItems = useMemo(() => {
          const items: any[] = []
          state.rows.forEach(row => {
               // 1. Ajout de la facture UNIQUE de prestation si elle existe
               const facture = state.factureFilesMap[row.id]
               if (facture) {
                    items.push({ file: facture, rowData: row, label: `Facture Presta` })
               }

               // 2. Ajout des justificatifs de frais MULTIPLES liés à cette ligne
               const expenses = state.expenseFilesMap[row.id] || []
               expenses.forEach((f, index) => {
                    items.push({ file: f, rowData: row, label: `Justificatif Frais ${index + 1}` })
               })
          })
          return items
     }, [state.rows, state.factureFilesMap, state.expenseFilesMap])

     /**
      * Calcul des indices pour la navigation (Précédent / Suivant).
      */
     const navigationIndices = useMemo(() => {
          if (!state.previewFile || state.previewFile.currentIndex === undefined) {
               return { prevIdx: -1, nextIdx: -1, current: 0, total: allPreviewableItems.length }
          }
          const index = state.previewFile.currentIndex
          return {
               prevIdx: index > 0 ? index - 1 : -1,
               nextIdx: index < allPreviewableItems.length - 1 ? index + 1 : -1,
               current: index + 1,
               total: allPreviewableItems.length,
          }
     }, [state.previewFile, allPreviewableItems])

     const handleNavigatePreview = (index: number) => {
          const item = allPreviewableItems[index]
          if (item) {
               if (state.previewFile?.url) URL.revokeObjectURL(state.previewFile.url)

               setters.setPreviewFile({
                    url: URL.createObjectURL(item.file),
                    name: item.file.name,
                    type: item.file.type,
                    rowData: item.rowData,
                    label: item.label,
                    currentIndex: index,
               })
          }
     }

     return (
          <div className="d-flex flex-column gap-8 p-4 p-lg-0 pb-20">
               {/* Étape 1 : Période */}
               <Step1MonthSelector
                    mois={state.mois}
                    onMonthChange={(m, f) => {
                         setters.setMois(m)
                         setters.setFormattedMois(f)
                    }}
               />

               {/* Étape 2 : Import */}
               {state.mois && (
                    <Step2FileDropzone
                         isExtracting={state.isExtracting}
                         isDragging={state.isDragging}
                         setIsDragging={setters.setIsDragging}
                         onDrop={handlers.handleFileDrop}
                         availableCount={state.listingQuery.data?.data?.length || 0}
                         fileInputRef={fileInputRef}
                    />
               )}

               <MassSaisieSubmissionError errors={state.submissionErrors} onClear={() => setters.setSubmissionErrors([])} />

               {/* Étape 3 : Tableau de révision */}
               {state.rows.length > 0 && (
                    <Step3ExtractionTable
                         rows={state.rows}
                         selectedRowIds={state.selectedRowIds}
                         expenseFilesMap={state.expenseFilesMap}
                         factureFilesMap={state.factureFilesMap} // Map de fichiers uniques
                         onToggleSelectAll={checked => {
                              const validIds = checked ? state.rows.filter(r => !!r.selectedConsultantId && Number(r.prestationHt) > 0).map(r => r.id) : []
                              setters.setSelectedRowIds(validIds)
                         }}
                         onToggleRow={id => setters.setSelectedRowIds(prev => (prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]))}
                         onUpdateRow={handlers.updateRow}
                         onRemoveRow={id => {
                              setters.setRows(prev => prev.filter(r => r.id !== id))
                              setters.setSelectedRowIds(prev => prev.filter(s => s !== id))
                         }}
                         // Gestion Facture Unique
                         onAttachFacture={handlers.handleAttachFactureFile}
                         onRemoveFactureFile={handlers.handleRemoveFactureFile}
                         onPreviewFactureFile={(id, file) => {
                              const row = state.rows.find(r => r.id === id)
                              if (row) {
                                   const idx = allPreviewableItems.findIndex(i => i.file === file && i.rowData.id === id)
                                   setters.setPreviewFile({
                                        url: URL.createObjectURL(file),
                                        name: file.name,
                                        type: file.type,
                                        rowData: row,
                                        currentIndex: idx,
                                        label: `Facture Presta`,
                                   })
                              }
                         }}
                         // Gestion Justificatifs Multiples
                         onAttachExpenses={handlers.handleAttachExpenseFiles}
                         onRemoveExpenseFile={handlers.handleRemoveExpenseFile}
                         onPreviewExpenseFile={(id, file) => {
                              const row = state.rows.find(r => r.id === id)
                              if (row) {
                                   const idx = allPreviewableItems.findIndex(i => i.file === file && i.rowData.id === id)
                                   setters.setPreviewFile({
                                        url: URL.createObjectURL(file),
                                        name: file.name,
                                        type: file.type,
                                        rowData: row,
                                        currentIndex: idx,
                                        label: `Justificatif Frais`,
                                   })
                              }
                         }}
                         consultants={state.listingQuery.data}
                         onSave={handlers.handleSave}
                         isSaving={state.isSaving}
                    />
               )}

               {/* Modale de visualisation */}
               <Step3ExtractionVerificationModal
                    previewFile={state.previewFile}
                    onClose={() => {
                         if (state.previewFile?.url) URL.revokeObjectURL(state.previewFile.url)
                         setters.setPreviewFile(null)
                    }}
                    mobileView={state.mobileView}
                    setMobileView={setters.setMobileView}
                    updateRow={handlers.updateRow}
                    consultants={state.listingQuery.data}
                    onNavigate={handleNavigatePreview}
                    navInfo={navigationIndices}
               />
          </div>
     )
}

export default SaisieEnMasseDesDeclarationsParRespFournisseur
